import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import clsx from "clsx";
import { Bars3Icon, ShoppingCartIcon } from "@heroicons/react/24/outline";
import type { RootState } from "@/store/store";
import Drawer from "@/components/ui/Drawer";
import { OFFICIAL_LINE } from "@/constants/common";

//#region type
interface HeaderProps {
  isNavigationHidden: boolean;
}
//#endregion

const Header = ({ isNavigationHidden }: HeaderProps) => {
  const cart = useSelector((store: RootState) => store.cart);

  const [isOpen, setIsOpen] = useState(false);

  const toggleMobileNav = (toOpen: boolean) => setIsOpen(toOpen);

  const cartQuantity = useMemo(
    () => cart?.items.reduce((acc, item) => acc + item.quantity, 0) || 0,
    [cart?.items],
  );

  return (
    <>
      {/* Mobile Header */}
      <header
        className={clsx(
          "sticky top-0 z-header flex items-center border-b border-neutral bg-white p-2 shadow-md lg:hidden",
          "justify-between",
        )}
      >
        <nav
          onClick={() => toggleMobileNav(true)}
          className={clsx(isNavigationHidden && "pointer-events-none invisible")}
        >
          <Bars3Icon className="box-content size-6 p-2" />
        </nav>
        <Logo />
        <CartBtn cartQuantity={cartQuantity} />
        <Drawer isOpen={isOpen} onToggle={toggleMobileNav}>
          <nav>
            <ol>{navItems}</ol>
          </nav>
        </Drawer>
      </header>

      {/* Desktop Header */}
      <header className="sticky top-0 z-header m-auto hidden w-full items-center justify-between border-b border-neutral bg-white p-3 lg:flex">
        <div className="brand-container flex items-center justify-between">
          <Logo />
          {!isNavigationHidden && (
            <nav>
              <ol className="flex items-center gap-4">{navItems}</ol>
            </nav>
          )}
        </div>
        <nav>
          <DesktopAuthItems cartQuantity={cartQuantity} />
        </nav>
      </header>
    </>
  );
};

// partial components
const Logo = () => (
  <Link to="/" className="w-48">
    <img
      src="/images/yuancoffee-logo-horizontal.svg"
      className="h-auto w-full"
      alt="小院咖啡 Logo"
    />
  </Link>
);

const CartBtn = ({ cartQuantity }: { cartQuantity: number }) => (
  <button className="relative mr-2 lg:mr-4">
    <Link to="/cart">
      <ShoppingCartIcon className="box-content size-6 p-2" />
      <div className="absolute right-0 top-0 flex size-4 items-center justify-center rounded-full bg-primary">
        <div className="text-[9px] text-white">{cartQuantity}</div>
      </div>
    </Link>
  </button>
);

const NavItem = ({
  link,
  children,
  target,
}: {
  link: string;
  children: ReactNode;
  target?: "_self" | "_blank";
}) => (
  <li className="flex items-center border px-4 py-4 text-base lg:border-none lg:px-4 lg:py-2 lg:text-sm">
    <Link to={link} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined}>
      {children}
    </Link>
  </li>
);

const DesktopAuthItems = ({ cartQuantity }: { cartQuantity: number }) => (
  <ol className="flex items-center gap-4">
    <li className="flex items-center">
      <CartBtn cartQuantity={cartQuantity} />
    </li>
  </ol>
);

const navItems = (
  <>
    <NavItem link="/shop">所有咖啡</NavItem>
    <NavItem link="/shopping-info">訂購說明</NavItem>
    <NavItem link="/order">查看訂單</NavItem>
    <NavItem link={OFFICIAL_LINE.LINK} target="_blank">
      LINE 詢問
    </NavItem>
  </>
);

export default Header;
